import React, { MouseEvent, KeyboardEvent } from 'react';
import { LoaderIcon } from '../../index';
import { withTracking } from '../../utils/tracking/tracking';
import { TrackingProps } from '../../utils/tracking/types';

enum EButtonVariant {
	primary = 'bg-primary text-gray-100 disabled:bg-gray-600 disabled:text-gray-400 border-2 border-gray-100 active:text-gray-100 visited:text-gray-100',
	secondary = 'text-gray-100 border-2 bg-transparent border-gray-350 hover:border-gray-100 focus-visible:border-gray-100 disabled:border-gray-600 disabled:text-gray-500 active:text-gray-100 visited:text-gray-100',
	shy = 'bg-white text-gray-200 border border-gray-600 hover:text-gray-100 focus-visible:text-gray-100  disabled:text-gray-500 active:text-gray-100 visited:text-gray-200 active:text-gray-100 visited:text-gray-200',
	overlay = 'border-none bg-black opacity-30 text-white disabled:text-gray-500 before:content-none hover:opacity-60 focus-visible:opacity-60  active:opacity-60 active:text-gray-100 visited:text-gray-100',
	tertiary = 'border-none text-gray-200 disabled:text-gray-500 before:content-none hover:text-gray-100 focus-visible:text-gray-100 active:text-gray-100 visited:text-gray-200',
	custom = '',
}

enum EButtonSize {
	xl = 'text-xl p-2',
	lg = 'text-lg p-2 md:text-xl md:p-3',
	md = 'text-lg p-2',
	smMd = 'text-base md:text-lg md:px-5 md:py-2.5',
	sm = 'text-base py-1.5',
	smMd = 'text-base md:text-lg md:px-5 md:py-2.5',
	xs = 'text-sm p-1',
	xxs = 'text-sm md:text-xs p-1',
}

type ButtonVariant = keyof typeof EButtonVariant;
type ButtonSize = keyof typeof EButtonSize;
interface IButtonProps {
	['data-cy']?: string;
	children?: React.ReactNode;
	variant?: ButtonVariant;
	size?: ButtonSize;
	disabled?: boolean;
	loading?: boolean;
	underline?: boolean;
	fullWidth?: boolean;
	href?: string;
	target?: string;
	className?: string;
	style?: React.CSSProperties;
	type?: 'button' | 'submit' | 'reset';
	tracking?: TrackingProps;
	onClick?: (ev: MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement>) => void;
	onMouseOver?: () => void;
	onMouseOut?: () => void;
}

const ButtonComponent = ({
	'data-cy': dataCy = 'button',
	variant = 'primary',
	size = 'md',
	disabled,
	fullWidth,
	children,
	className,
	loading,
	href,
	underline = false,
	target = '_self',
	type,
	style = {},
	onClick,
	onMouseOut,
	onMouseOver,
}: IButtonProps) =>
	href ? (
		<a
			data-cy={dataCy}
			className={`cursor-pointer focus-visible:outline-black focus-visible:outline focus-visible:outline-[3px] outline-offset-4 focus-visible:outline-black ${
				underline ? 'underline' : 'no-underline'
			} border-solid inline-flex items-center justify-center rounded font-bold relative transition ease-in-out duration-300 ${
				EButtonVariant[variant]
			} ${EButtonSize[size]} ${
				fullWidth && 'w-full'
			} disabled:pointer-events-none before:content-['*'] before:absolute before:bg-black before:w-full before:h-full before:opacity-0 before:border-solid before:border before:rounded before:transition before:ease-in-out before:duration-300 before:hover:opacity-5 before:focus-visible:opacity-5 before:active:opacity-5 ${className}`}
			style={style}
			href={href}
			target={target}
			onClick={onClick}
			onMouseOver={onMouseOver}
			onMouseOut={onMouseOut}>
			{children}
		</a>
	) : (
		<button
			data-cy={dataCy}
			disabled={disabled}
			role="button"
			className={`cursor-pointer border-solid normal-case inline-flex items-center justify-center rounded font-bold relative transition ease-in-out duration-300 no-underline focus-visible:outline-black focus-visible:outline focus-visible:outline-[3px] outline-offset-4 focus-visible:outline-black ${
				EButtonVariant[variant]
			} ${EButtonSize[size]} ${
				fullWidth && 'w-full'
			} disabled:pointer-events-none before:content-['*'] before:rounded before:absolute before:bg-black before:w-full before:h-full before:opacity-0 before:border before:border-solid before:transition before:ease-in-out before:duration-300 before:hover:opacity-5 before:focus-visible:opacity-5 before:active:opacity-5 ${
				loading ? 'text-opacity-30 pointer-events-none' : ''
			} ${className}`}
			style={style}
			type={type}
			tabIndex={disabled ? -1 : 0}
			onClick={onClick}
			onMouseOver={onMouseOver}
			onMouseOut={onMouseOut}>
			{loading && (
				<span className="mr-1.5 animate-spin inline-flex">
					<LoaderIcon />
				</span>
			)}
			{children}
		</button>
	);

export const Button = withTracking(ButtonComponent);
